import { useTranslation } from 'react-i18next';
import EmptyListResult from '../../components/EmptyListResult/EmptyListResult';
import { ActionIcon, Badge, Grid, Skeleton, Tooltip } from '@mantine/core';
import { useHistory } from 'react-router-dom';
import {
  IconBrandWhatsapp,
  IconCircleCheck,
  IconCircleDashedCheck,
  IconCurrencyDollar,
  IconEdit,
  IconPhoto,
  IconPrinter,
  IconReceiptDollar,
  IconShirt,
  IconTrash,
  IconUpload,
  IconWash
} from '@tabler/icons-react';
import { useState } from 'react';
import CatalogCard from '../../components/CatalogCard/CatalogCard';
import moment from 'moment';
import InfiniteScroll from 'react-infinite-scroll-component';
import { formatMoneyString, getCustomerFullName, getWhatsappURLString } from '../../utils/strings';
import { IOrder } from '../../interfaces/Order';
import OrderStatuses from '../../enum/OrderStatuses';
import { utils as wdUtils } from 'washday-sdk';

import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal';
import { IOrderProduct } from '../../interfaces/Product';
import { buildOrderFolio, getOrderCardColorsByAmountDueAndStatus, isOrderPartiallyCollected, normalizeOrderScheduleTimeValue } from '../../utils/ordersUtils';
import { getOrderAmountDue } from '@washday/utilities';
import OrdersCardMoreActionsMenu from '../OrdersCardMoreActionsMenu';
import PrintTicketDrawer from '../PrintTicketDrawer';
import { IStore } from '../../interfaces/Store';
import { getPiecesBreakdown, formatPiecesBreakdown } from '../../utils/helpers/getAllPieces';
import { useDisclosure } from '@mantine/hooks';
import useCanEditOrders from '../../hooks/useCanEditOrders';
import useCanValidateStoreReception from '../../hooks/useCanValidateStoreReception';
import {
  STORE_RECEPTION_VALIDATION_QUERY_MODE,
  canShowValidateStoreReceptionAction,
  getOrderStoreId
} from '../../utils/storeReceptionValidation';

const OrdersCardView = ({
  searchParamStoreId,
  storeReceptionValidationEnabled,
  onOrderCancelled,
  onSendEmailReceipt,
  onDownloadPNGTicket,
  showPaymentLinesModal,
  showOrderProductsModal,
  onOrderDelivered,
  onOrderCleaning,
  orders = [],
  fetchNextPage = () => {},
  hasNextPage = false,
  onShowCreateOrderEvidenceModal,
  onShowOrderEvidenceModal
}: {
  orders: any;
  fetchNextPage: any;
  hasNextPage: boolean;
  searchParamStoreId: string;
  onOrderDelivered: ({
    orderId,
    sequence,
    storeId
  }: {
    orderId: string;
    sequence: string;
    storeId: string;
  }) => Promise<void>;
  onOrderCleaning: ({ sequence, storeId }: { sequence: string; storeId: string }) => Promise<void>;
  onOrderCancelled: ({ sequence, storeId }: { sequence: string; storeId: string }) => Promise<void>;
  onSendEmailReceipt: (orderId: string) => Promise<void>;
  onPrintTicket: (order: IOrder) => Promise<void>;
  onDownloadPNGTicket: (order: IOrder) => Promise<void>;
  showPaymentLinesModal: (order: IOrder) => void;
  showOrderProductsModal: (order: IOrder) => void;
  onShowCreateOrderEvidenceModal: (order: IOrder) => void;
  onShowOrderEvidenceModal: (order: IOrder) => void;
  storeReceptionValidationEnabled: boolean;
}) => {
  const { t } = useTranslation([
    'FieldName',
    'CommonButtons',
    'Notifications',
    'OrdersPage',
    'CleaningPage',
    'UpdateOrderForm',
    'ReportsPage',
    'OrdersHeaderSummary'
  ]);
  const history = useHistory();
  const canEditOrders = useCanEditOrders();
  const canValidateStoreReception = useCanValidateStoreReception();
  const [openedPrintTicketDrawer, { open: openPrintTicketDrawer, close: closePrintTicketDrawer }] =
    useDisclosure(false);
  const [printTicketOrder, setPrintTicketOrder] = useState<IOrder | null>(null);

  const handleOpenPrintTicketDrawer = (order: IOrder) => {
    setPrintTicketOrder(order);
    openPrintTicketDrawer();
  };

  const handleClosePrintTicketDrawer = () => {
    closePrintTicketDrawer();
    setPrintTicketOrder(null);
  };

  return (
    <>
      <PrintTicketDrawer
        opened={openedPrintTicketDrawer}
        onClose={handleClosePrintTicketDrawer}
        order={printTicketOrder}
      />
      {orders && orders.length === 0 && <EmptyListResult />}
      <InfiniteScroll
        dataLength={orders?.length || 0}
        next={fetchNextPage}
        hasMore={hasNextPage}
        style={{ overflow: 'hidden', position: 'relative' }}
        loader={
          <>
            <Skeleton height={10} my={10} radius="md" />
            <Skeleton height={10} my={10} radius="md" />
            <Skeleton height={10} my={10} radius="md" />
          </>
        }
        // endMessage={<Empty description={false} />}
      >
        <>
          <Grid gutter="md" pos={'relative'}>
            {orders?.map((order: IOrder) => {
              const orderStoreId = getOrderStoreId(order, searchParamStoreId);
              const cardActions = [
                {
                  action: () => onShowCreateOrderEvidenceModal(order),
                  disabled: false,
                  Icon: <IconUpload />,
                  tooltip: t('OrdersPage:evidenceModal.uploadTooltip')
                },
                {
                  action: () => showPaymentLinesModal(order),
                  disabled: false,
                  Icon: <IconCurrencyDollar />,
                  tooltip: t('OrdersPage:paymentLinesModal.tooltip')
                },
                {
                  action: () => showOrderProductsModal(order),
                  disabled: false,
                  Icon: <IconShirt />,
                  tooltip: t('OrdersPage:productsModal.tooltip')
                },
                {
                  action: () =>
                    history.push(
                      `/orders/${order._id}?storeId=${orderStoreId}&mode=${STORE_RECEPTION_VALIDATION_QUERY_MODE}`,
                      {
                        storeID: orderStoreId
                      }
                    ),
                  hidden: !canShowValidateStoreReceptionAction({
                    order,
                    canValidateStoreReception,
                    storeReceptionValidationEnabled
                  }),
                  disabled: false,
                  Icon: <IconCircleDashedCheck />,
                  tooltip: t('OrdersPage:validateStoreReception')
                },
                {
                  action: () =>
                    history.push(`/orders/${order._id}?storeId=${searchParamStoreId}`, {
                      storeID: searchParamStoreId
                    }),
                  disabled: !canEditOrders,
                  Icon: <IconEdit />,
                  tooltip: t('CommonButtons:edit')
                }
              ];

              if (order.status === OrderStatuses.DELIVERING) {
                cardActions.push({
                  action: () =>
                    ConfirmationModal({
                      title: t('Notifications:CRUD.deliveredConfirmation'),
                      onConfirm: () =>
                        onOrderDelivered({
                          orderId: order._id || '',
                          sequence: order.sequence || '',
                          storeId: order.createdIn?._id || ''
                        }),
                      onCancel: () => {}
                    }),
                  disabled: false,
                  Icon: <IconCircleCheck />,
                  tooltip: 'Pedido Entregado'
                });
              } else if (order.status === OrderStatuses.PICKINGUP) {
                cardActions.push({
                  action: () =>
                    ConfirmationModal({
                      title: t('Notifications:CRUD.cleaningConfirmation'),
                      onConfirm: () =>
                        onOrderCleaning({
                          sequence: order.sequence || '',
                          storeId: order.createdIn?._id || ''
                        }),
                      onCancel: () => {}
                    }),
                  disabled: false,
                  Icon: <IconWash />,
                  tooltip: 'Pedido en Proceso'
                });
              }

              cardActions.push({
                action: () =>
                  ConfirmationModal({
                    title: t('Notifications:CRUD.cancelConfirmation'),
                    onConfirm: () =>
                      onOrderCancelled({
                        sequence: order.sequence || '',
                        storeId: order.createdIn?._id || ''
                      }),
                    onCancel: () => {}
                  }),
                disabled: false,
                Icon: <IconTrash />,
                tooltip: 'Cancelar Pedido'
              });

              const orderProducts = order?.products || [];
              const orderBuyAndGerProducts = order?.buyAndGetProducts || [];
              const allPieces = formatPiecesBreakdown(getPiecesBreakdown(orderProducts, orderBuyAndGerProducts), {
                measurementUnits: (order.createdIn as IStore)?.measurementUnits
              });
              const amountDue = getOrderAmountDue(order);
              const customColors = getOrderCardColorsByAmountDueAndStatus({
                amountDue,
                highlightOrderCardsByPaymentPending: (order.createdIn as Partial<IStore>)
                  ?.ordersDisplayConfig?.highlightOrderCardsByPaymentPending,
                status: order.status
              });

              let pickUpData = t('FieldName:pickupNotRequired');
              if (order.pickup && order.pickupInfo) {
                const pickupDate = moment(order.pickupInfo.date).format('DD/MM/YYYY');
                const pickupFromTime = normalizeOrderScheduleTimeValue(order.pickupInfo.fromTime);
                const pickuptoTime = normalizeOrderScheduleTimeValue(order.pickupInfo.toTime);
                pickUpData = `${pickupDate} ${pickupFromTime} - ${pickuptoTime}`;
              }

              let deliveryData = t('FieldName:pickupNotRequired');
              if (order.deliveryInfo) {
                const deliveryDate = moment(order.deliveryInfo.date).format('DD/MM/YYYY');
                const deliveryFromTime = normalizeOrderScheduleTimeValue(order.deliveryInfo.fromTime);
                const deliverytoTime = normalizeOrderScheduleTimeValue(order.deliveryInfo.toTime);
                deliveryData = `${deliveryDate} ${deliveryFromTime} - ${deliverytoTime}`;
              }

              return (
                <Grid.Col key={order._id} span={{ xs: 12, sm: 6, lg: 4 }}>
                  <CatalogCard
                    fields={[
                      {
                        label: t('FieldName:pieces'),
                        value: allPieces
                      },
                      {
                        label: t('FieldName:name'),
                        value: getCustomerFullName(order.customer.name, order.customer.lastName)
                      },
                      {
                        label: t('FieldName:total'),
                        value: formatMoneyString((order.total || 0).toFixed(2))
                      },
                      {
                        label: t('FieldName:amountDue'),
                        value: formatMoneyString(amountDue.toFixed(2))
                      },
                      {
                        label: t('FieldName:status'),
                        value: (
                          <>
                            {t(`FieldName:orderStatuses.${OrderStatuses[order.status.toUpperCase()]}`)}
                            {isOrderPartiallyCollected(order) && (
                              <Badge component="span" color="orange" size="xs" variant="light" ml={4}>
                                {t('FieldName:partialDeliveryBadge')}
                              </Badge>
                            )}
                          </>
                        )
                      },
                      {
                        label: t('FieldName:pickupDate'),
                        value: pickUpData || '',
                        lineClamp: 2
                      },
                      {
                        label: t('FieldName:deliveryDate'),
                        value: deliveryData || '',
                        lineClamp: 2
                      },
                      {
                        label: t('FieldName:createdDate'),
                        value: moment(order.createdDate).format('DD/MM/YYYY HH:mm') || ''
                      },
                      {
                        label: t('FieldName:address'),
                        value:
                          wdUtils.getFormattedAddress({
                            street: order.address?.street,
                            zipCode: order.address?.zipCode,
                            city: order.address?.city,
                            state: order.address?.state
                          }) || '',
                        valueTooltip: wdUtils.getFormattedAddress({
                          street: order.address?.street,
                          zipCode: order.address?.zipCode,
                          city: order.address?.city,
                          state: order.address?.state
                        })
                      },
                      {
                        label: t('FieldName:notes'),
                        value: order.notes || '',
                        valueTooltip: order.notes
                      }
                    ]}
                    cardActionButtons={cardActions}
                    cardTitle={buildOrderFolio(order?.folioPrefix, order?.sequence) || ''}
                    cardTitleExtra={
                      <>
                        {(order?.evidence?.length ?? 0) > 0 && (
                          <Tooltip
                            withinPortal={false}
                            label={t('OrdersPage:evidenceModal.viewTooltip')}>
                            <ActionIcon
                              color={customColors.headerTextColor}
                              onClick={() => onShowOrderEvidenceModal(order)}
                              variant="transparent"
                              aria-label={t('OrdersPage:evidenceModal.viewTooltip')}>
                              <IconPhoto />
                            </ActionIcon>
                          </Tooltip>
                        )}
                        <Tooltip withinPortal={false} label="Imprimir Ticket">
                          <ActionIcon
                            color={customColors.headerTextColor}
                            onClick={() => handleOpenPrintTicketDrawer(order)}
                            variant="transparent"
                            aria-label="Imprimir Ticket">
                            <IconPrinter />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip
                          withinPortal={false}
                          label={
                            order.customer?.phone
                              ? t('CommonButtons:sendWhatsappMessage')
                              : t('CommonButtons:noCustomerPhone')
                          }>
                          <ActionIcon
                            color={customColors.headerTextColor || 'green'}
                            disabled={!order.customer.phone}
                            onClick={() => {
                              window.open(
                                getWhatsappURLString(
                                  order.customer?.phone,
                                  order.customer?.phoneCountryCode
                                ),
                                '_blank'
                              );
                            }}
                            variant="transparent"
                            aria-label={
                              order.customer?.phone
                                ? t('CommonButtons:sendWhatsappMessage')
                                : t('CommonButtons:noCustomerPhone')
                            }>
                            <IconBrandWhatsapp />
                          </ActionIcon>
                        </Tooltip>
                        <OrdersCardMoreActionsMenu order={order} customColors={customColors} />
                      </>
                    }
                    TitleIcon={IconReceiptDollar}
                    customColors={customColors}
                  />
                </Grid.Col>
              );
            })}
          </Grid>
        </>
      </InfiniteScroll>
    </>
  );
};

export default OrdersCardView;
